import debounce from 'lodash/debounce';

import { init as initLocationGoogleAutocomplete } from '../location-page.google-autocomplete';
import { parseSearch } from './free-text-search-parser';
import { exampleSearches, loadData } from './own-words-search.data';
import { highlightRecognised } from './own-words-search.highlight';
import {
  findCityKey,
  findCityKeyByAreaInText,
  findCityKeyByLocationId,
  MARKET_SUBDIRS,
  resolveSearch,
} from './own-words-search.url';

const VERSION_OWN_WORDS = 1;

const SECTION = 'header_free_text';

/** Mixpanel keeps up to 255 characters of a property value. */
const MAX_TRACKED_TEXT = 255;

/** Where the hero form stacks its fields and the box becomes a textarea – the same breakpoint as the control form. */
const STACKED_LAYOUT_QUERY = '(max-width: 1000px)';

const MAX_TEXTAREA_LINES = 3;

/** How long a Google place picked in the Location field may take to resolve to bounds before searching anyway. */
const PLACE_DETAILS_WAIT_MS = 1000;

const track = (element, interaction, value = '') => {
  App.track.event('location_page_interaction', {
    section: SECTION,
    element,
    element_value: value,
    interaction,
  });
};

const todayIso = () => {
  const today = new Date();
  const pad = (number) => String(number).padStart(2, '0');

  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
};

const loadDataOrNull = async () => {
  try {
    return await loadData();
  } catch (error) {
    window.Rollbar?.error('Own-words search: vocabulary failed to load, searching without filters', error);

    return null;
  }
};

const waitForPlaceDetails = async () => {
  const startedAt = Date.now();
  while (window.googleAutoCompleteLocationBoundsIsLoading && Date.now() - startedAt < PLACE_DETAILS_WAIT_MS) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, 100);
    });
  }
};

/**
 * @param {HTMLElement} form
 * @param {boolean} withLocationField Home page: the text sits next to a Location field instead of replacing it
 */
const initOwnWordsSearch = (form, withLocationField) => {
  const field = form.querySelector('.js-own-words-field');
  const inputs = Array.from(form.querySelectorAll('.js-own-words-input'));
  const textarea = inputs.find((input) => input.tagName === 'TEXTAREA');
  const clamp = form.querySelector('.js-own-words-clamp');
  const clearButton = form.querySelector('.js-own-words-clear');
  const dropdown = form.querySelector('.js-own-words-dropdown');
  const searchButton = form.querySelector('.js-hero-search');
  const country = window.__TGV_ISO_COUNTRY_CODE;
  const examples = exampleSearches(country);
  const locationField = withLocationField ? initLocationGoogleAutocomplete() : null;
  const $locationInput = withLocationField ? $('#hero-location') : null;

  let text = '';
  let rows = [];
  let activeIndex = -1;
  let isOpen = false;
  let isFocused = false;
  let data = null;

  const isStacked = () => window.matchMedia(STACKED_LAYOUT_QUERY).matches;
  const visibleInput = () => inputs.find((input) => input.offsetParent !== null) ?? inputs[0];

  const cityKeyFromName = (name) => (name && data ? findCityKey(name, country, data.vocab) : null);

  // ---------------------------------------------------------------- where the text is read against

  const currentHomeLocation = () => {
    const formData = window.__TGV_SEARCH_FORM_DATA;
    const label = String($locationInput.val() ?? '').trim();

    if (window.googleAutoCompleteLocationBounds) {
      const { subdir, ...bounds } = window.googleAutoCompleteLocationBounds;

      return {
        label,
        locationId: null,
        location: {
          subdir: subdir || formData.subdir,
          params: [
            ...Object.entries(bounds).map(([key, value]) => [key, String(value)]),
            ['neighbourhood', window.googleAutoCompleteLocationFormattedAddress ?? label],
          ],
        },
      };
    }

    const selection = $locationInput.data('getSelection')?.();
    const locationId = selection?.data?.locationId ?? formData.locationId;

    if (!label || !locationId) {
      return null;
    }

    return {
      label,
      locationId,
      location: { subdir: selection?.data?.subdir || formData.subdir, params: [['location_id', String(locationId)]] },
    };
  };

  const homeCityKey = (homeLocation) => {
    if (!homeLocation || !data) {
      return null;
    }

    return (homeLocation.locationId ? findCityKeyByLocationId(homeLocation.locationId, data.locationIds) : null)
      ?? cityKeyFromName(homeLocation.label.split(',')[0]);
  };

  /**
   * @returns {import('./own-words-search.url').SearchPage}
   */
  const searchPage = (homeLocation) => {
    if (withLocationField) {
      // a city named in the text has already been moved into the Location field
      return { country, cityKey: homeCityKey(homeLocation), followsOtherCity: false, location: homeLocation.location };
    }

    const formData = window.__TGV_SEARCH_FORM_DATA;
    const cityKey = (data ? findCityKeyByLocationId(formData.locationId, data.locationIds) : null)
      ?? cityKeyFromName(window.__TGV_GA4_TRACKING?.city);

    return {
      country,
      cityKey,
      followsOtherCity: true,
      location: { subdir: formData.subdir, params: [['location_id', String(formData.locationId)]] },
    };
  };

  const parse = (value, cityKey) => {
    if (!data) {
      return null;
    }

    try {
      return parseSearch(value, {
        lpCity: (cityKey && data.vocab.locations[cityKey]?.city) || '',
        country,
        today: todayIso(),
        lpEventSlug: null,
      }, data.vocab);
    } catch (error) {
      // the typed text is the Client's own words, so it stays out of the error report
      window.Rollbar?.error('Own-words search: parser failed, searching without filters', error);

      return null;
    }
  };

  const textCityKey = () => (withLocationField ? homeCityKey(currentHomeLocation()) : searchPage(null).cityKey);

  // ---------------------------------------------------------------- the box

  const resizeTextarea = () => {
    if (textarea.offsetParent === null) {
      return;
    }

    const maxHeight = parseFloat(getComputedStyle(textarea).lineHeight) * MAX_TEXTAREA_LINES;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = isFocused && textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  const updateClamp = () => {
    field.classList.toggle('has-clamp', !isFocused && text !== '');
  };

  const setText = (value) => {
    // Enter searches, so a pasted line break becomes a space
    text = value.replace(/\s*\n\s*/g, ' ');
    inputs.forEach((input) => {
      if (input.value !== text) {
        input.value = text;
      }
    });
    clearButton.hidden = text === '';
    clamp.textContent = text;
    resizeTextarea();
    updateClamp();
  };

  // ---------------------------------------------------------------- the dropdown

  const setExpanded = (expanded) => {
    inputs.forEach((input) => input.setAttribute('aria-expanded', String(expanded)));
  };

  const positionDropdown = () => {
    // Stacked Location Page: hangs off the text area and covers the Search button. Elsewhere: under the whole bar.
    const top = isStacked() && !withLocationField ? field.offsetTop + field.offsetHeight : form.clientHeight;
    dropdown.style.top = `${top}px`;
  };

  const renderRows = () => {
    dropdown.replaceChildren(...rows.map((row, index) => {
      const option = document.createElement('div');
      option.className = 'c-own-words__option';
      option.classList.toggle('c-own-words__option--typed', row.element === 'typed_suggestion');
      option.classList.toggle('is-active', index === activeIndex);
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(index === activeIndex));
      option.dataset.index = String(index);

      const label = document.createElement('span');
      label.className = 'c-own-words__option-text';
      if (row.html) {
        label.innerHTML = row.html; // escaped by highlightRecognised()
      } else {
        label.textContent = row.text;
      }
      option.append(label);

      return option;
    }));
    positionDropdown();
  };

  const highlightTyped = debounce(() => {
    const row = rows[0];
    if (!isOpen || row?.element !== 'typed_suggestion' || !data) {
      return;
    }

    const cityKey = textCityKey();
    row.html = highlightRecognised(row.text, (value) => parse(value, cityKey));
    renderRows();
  }, 150);

  const fillRows = () => {
    rows = text === ''
      ? examples.map((example) => ({ text: example, html: null, element: 'example_suggestion' }))
      : [{ text, html: null, element: 'typed_suggestion' }];
    activeIndex = -1;
    renderRows();
    highlightTyped();
  };

  const open = () => {
    fillRows();
    dropdown.hidden = false;
    form.classList.add('is-open');
    isOpen = true;
    setExpanded(true);
  };

  const close = () => {
    highlightTyped.cancel();
    dropdown.hidden = true;
    form.classList.remove('is-open');
    isOpen = false;
    activeIndex = -1;
    setExpanded(false);
  };

  const moveActive = (step) => {
    if (!isOpen) {
      open();
    }
    const positions = rows.length + 1; // every row, plus -1: back in the box
    activeIndex = ((activeIndex + 1 + step + positions) % positions) - 1;
    renderRows();
  };

  const pick = (index) => {
    const row = rows[index];
    track(row.element, 'click', row.element === 'example_suggestion' ? row.text : '');
    setText(row.text);
    close();

    const input = visibleInput();
    input.focus();
    input.setSelectionRange(text.length, text.length);
  };

  // ---------------------------------------------------------------- Home page: the Location field follows the text

  const autofillLocation = debounce(async () => {
    if (text.trim() === '') {
      return;
    }

    data = await loadDataOrNull();
    if (!data) {
      return;
    }

    const homeLocation = currentHomeLocation();
    const result = parse(text, homeCityKey(homeLocation));
    const otherCity = result?.intent === 'venue_search' ? result.location.other_city : null;

    let cityKey = otherCity ? findCityKey(otherCity, country, data.vocab) : null;
    if (!cityKey && String($locationInput.val() ?? '').trim() === '') {
      const defaultCityKey = findCityKeyByLocationId(window.__TGV_SEARCH__DEFAULT_LOCATION_ID, data.locationIds);
      cityKey = findCityKeyByAreaInText(text, country, data.vocab, defaultCityKey);
    }

    const cityId = cityKey ? data.locationIds[cityKey]?.id : null;
    if (!cityId || cityId === Number(homeLocation?.locationId)) {
      return;
    }

    const [, cityCountry] = cityKey.split('|');
    const { city } = data.vocab.locations[cityKey];
    locationField.setLocation({ label: city, locationId: cityId, subdir: MARKET_SUBDIRS[cityCountry] });
    track('location_autofill', 'change', city);
  }, 300);

  // ---------------------------------------------------------------- search

  const search = async (trigger) => {
    const typed = text.trim();
    track(trigger, 'click', typed.slice(0, MAX_TRACKED_TEXT));
    close();

    let homeLocation = null;
    if (withLocationField) {
      await autofillLocation.flush();
      await waitForPlaceDetails();
      locationField.validateIsEmpty();
      homeLocation = locationField.isValid() ? currentHomeLocation() : null;

      if (!homeLocation) {
        track('location_error', 'visible');

        return;
      }
    }

    if (typed !== '') {
      data = await loadDataOrNull();
    }

    const page = searchPage(homeLocation);
    const result = typed === '' ? null : parse(typed, page.cityKey);
    const target = resolveSearch(result, page, data?.vocab, data?.locationIds);

    App.track.event('clicked_to_search_page', {
      section: SECTION,
      element: trigger,
      element_value: target.roomTag,
      interaction: 'click',
    });

    const url = new URL(App.es6.shared.fill_variables(window.__TGV_ROUTE_SEARCH, {
      roomTag: target.roomTag,
      subdir: target.subdir,
    }));
    target.params.forEach(([key, value]) => url.searchParams.append(key, value));

    window.location.href = url.href;
  };

  // ---------------------------------------------------------------- events

  inputs.forEach((input) => {
    input.addEventListener('focus', () => {
      isFocused = true;
      updateClamp();
      resizeTextarea();
      open();
      track('search_box', 'click');
      loadDataOrNull().then((loaded) => {
        data = loaded;
        highlightTyped();
      });
    });

    input.addEventListener('blur', () => {
      isFocused = false;
      resizeTextarea();
      updateClamp();
    });

    input.addEventListener('input', () => {
      setText(input.value);
      if (isOpen) {
        fillRows();
      } else {
        open();
      }
      if (withLocationField) {
        autofillLocation();
      }
    });

    input.addEventListener('keydown', (event) => {
      if (event.isComposing) {
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (isOpen && activeIndex >= 0) {
          pick(activeIndex);
        } else {
          search('enter_key');
        }
      } else if (event.key === 'Escape' || event.key === 'Tab') {
        close();
      }
    });
  });

  field.addEventListener('click', (event) => {
    if (!event.target.closest('.js-own-words-input, .js-own-words-clear')) {
      visibleInput().focus();
    }
  });

  // keeps the focus in the box, so clearing doesn't count as a new click into it
  clearButton.addEventListener('mousedown', (event) => event.preventDefault());
  clearButton.addEventListener('click', () => {
    track('clear_button', 'click');
    setText('');
    visibleInput().focus();
    open();
  });

  // keeps the focus in the box, so picking a row doesn't count as leaving it
  dropdown.addEventListener('mousedown', (event) => event.preventDefault());
  dropdown.addEventListener('click', (event) => {
    const option = event.target.closest('[role="option"]');
    if (option) {
      pick(Number(option.dataset.index));
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (isOpen && !form.contains(event.target)) {
      close();
    }
  });

  searchButton.addEventListener('click', (event) => {
    event.preventDefault();
    search('search_button');
  });

  window.addEventListener('resize', debounce(() => {
    resizeTextarea();
    if (isOpen) {
      positionDropdown();
    }
  }, 100));

  track('search_box', 'visible');
};

/**
 * Generic Location Pages and the Home page, all markets. The version is allocated by the inline script in
 * location_pages/partial/search-form.twig before the form renders, so the B fields never flash in after the control.
 *
 * @todo TM-8427 keep the winning search form and remove the other once the experiment is settled
 * @returns {boolean} true when the visitor is in B and this module now runs the search form
 */
export const initOwnWordsSearchExperiment = () => {
  const experiment = window.__TGV_OWN_WORDS_SEARCH;
  const form = document.querySelector('.js-own-words-form');

  if (!experiment || !form || experiment.version === null) {
    return false;
  }

  // both versions differ from the moment the hero renders, so every allocated visitor is counted on load
  App.track.event('experiment_impression', {
    experiment_id: experiment.experimentId,
    variant_id: experiment.version,
  });

  if (experiment.version !== VERSION_OWN_WORDS) {
    return false;
  }

  initOwnWordsSearch(form, experiment.withLocationField);

  return true;
};
